import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { array, number, object, string } from "yup";
import { CourseFormValues } from "src/models/Course.model";
import CourseForm from "../Layouts/CourseForm";
import {
  useEditCourseMutation,
  useGetCourseByIdQuery,
} from "src/services/CourseServices";
import { showToast } from "src/utils/showToaster";
import { CircularProgress } from "@mui/material";

type Props = {
  onClose: () => void;
  courseId: string;
};

const EditCourseFormWrapper = ({ onClose, courseId }: Props) => {
  const [editCourse] = useEditCourseMutation();
  const {
    data: courseDataById,
    isLoading: isCourseLoading,
    isFetching: isCourseFetching,
  } = useGetCourseByIdQuery({
    id: courseId,
  });

  const [courseData, setCourseData] = useState<any>([]);

  useEffect(() => {
    if (!isCourseFetching && !isCourseLoading) {
      setCourseData(courseDataById?.data);
    }
  }, [courseDataById?.data, isCourseLoading, isCourseFetching]);

  // Form Initial Values
  const initialValues: CourseFormValues = {
    courseName: courseData?.courseName || "",
    courseDescription: courseData?.courseDescription || "",
    moduleId:
      courseData?.moduleId?.map((module: any) => {
        return {
          label: module?.moduleName,
          value: module?._id,
        };
      }) || [],
    classroomFees: courseData?.classroomFees || "",
    onlineFees: courseData?.onlineFees || "",
  };

  // Validation Schema
  const validationSchema = object().shape({
    courseName: string().required("Please enter course name"),
    courseDescription: string().required("Please enter course description"),
    moduleId: array()
      .min(1, "Please select modules")
      .required("Please select modules"),
    classroomFees: number()
      .typeError("Please enter valid fees")
      .required("Please enter fees"),
    onlineFees: number()
      .typeError("Please enter valid fees")
      .required("Please enter fees"),
  });

  // Handle Submit
  const handleSubmit = (
    values: CourseFormValues,
    { setSubmitting, resetForm }: FormikHelpers<CourseFormValues>
  ) => {
    const formattedValues = {
      ...values,
      moduleId: values?.moduleId?.map((module) => module?.value),
    };
    editCourse({
      body: formattedValues,
      id: courseId,
    }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
          onClose();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formikProps) => (
          <Form>
            {(isCourseLoading || isCourseFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <CourseForm
              formikProps={formikProps}
              onClose={onClose}
              formType="Edit"
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditCourseFormWrapper;
